import { AfterViewInit, Component, effect, ElementRef, inject, input, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyTheme, StyleService } from '@vendasta/galaxy/style-service';
import { WhatsAppConnectionService } from './whatsapp-connection.service';

@Component({
  selector: 'inbox-whatsapp-login',
  imports: [MatButtonModule, TranslateModule, GalaxySnackbarModule],
  templateUrl: './whatsapp-login.component.html',
  styleUrl: './whatsapp-login.component.scss',
})
export class WhatsappLoginComponent implements AfterViewInit {
  @ViewChild('loginIFrame') loginIFrame!: ElementRef<HTMLIFrameElement>;

  protected iframeSrc = input.required<string>();

  protected safeIFrameSrc = signal<SafeResourceUrl | null>(null);
  private initialized = signal(false);
  private loginButtonText = toSignal<string>(
    inject(TranslateService).stream('INBOX.SETTINGS.WHATSAPP_MESSAGES.LOGIN_WITH_FACEBOOK'),
  );
  private theme = toSignal<GalaxyTheme>(inject(StyleService).activeTheme$);

  private readonly domSanitizer = inject(DomSanitizer);
  private readonly whatsAppConnectionService = inject(WhatsAppConnectionService);
  private readonly snackbarService = inject(SnackbarService);

  constructor() {
    effect(() => {
      if (this.initialized()) {
        this.sendMessage({ type: 'SET_BUTTON_TEXT', text: this.loginButtonText() });
      }
    });

    effect(() => {
      if (this.initialized()) {
        this.sendMessage({ type: 'SET_THEME', theme: this.theme() });
      }
    });
  }

  ngAfterViewInit(): void {
    window.addEventListener('message', this.receiveMessage.bind(this));
    this.safeIFrameSrc.set(this.domSanitizer.bypassSecurityTrustResourceUrl(this.iframeSrc()));
  }

  sendMessage(message: unknown): void {
    if (!this.initialized()) {
      return;
    }
    this.loginIFrame.nativeElement.contentWindow?.postMessage(JSON.stringify(message), this.iframeSrc());
  }

  async receiveMessage(event: MessageEvent) {
    if (!event.origin) {
      return;
    }

    if (!this.initialized()) {
      let payload = null;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }

      if (!payload) {
        return;
      }

      if (payload.type === 'READY') {
        this.loginIFrame.nativeElement.contentWindow?.postMessage(
          JSON.stringify({ type: 'SET_ORIGIN', origin: window.origin }),
          this.iframeSrc(),
        );
        this.initialized.set(true);
      }
    } else {
      if (!this.iframeSrc().startsWith(event.origin)) {
        return;
      }

      let payload = null;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }

      if (!payload) {
        return;
      }

      if (payload.type === 'LOGIN_SUCCESS') {
        const { wabaID, phoneNumberID, code } = payload;
        try {
          const message = await this.whatsAppConnectionService.connect(wabaID, phoneNumberID, code);
          if (message !== '') {
            this.snackbarService.openSuccessSnack(message);
          }
        } catch (err: unknown) {
          if (err instanceof Error) {
            this.snackbarService.openErrorSnack(err.message);
          }
        }
      }
    }
  }
}
